import React, { useEffect, useState, useRef, useContext, createContext } from "react";
import "./App.css";
import { T, LANGUAGES, getStoredLang, applyLangToDocument, saveLang, fmt } from "./i18n";
import TricksLayer from "./Tricks";

const LangContext = createContext({ lang: "en", t: T.en });
const useLang = () => useContext(LangContext);

const GITHUB_USER = "chouaibcher";

// Project text (subtitle, description, details) lives in i18n.js under t.projects[id].
const projects = {
  web: [
    { id: "android", title: "Android Boot Simulator", link: "#android-boot", image: "./androidPrj.jpeg" },
    {
      id: "youngme",
      title: "YoungME Lavoro",
      link: "https://youngmelavoro.it",
      video: "./youngmelavoro-preview.mp4",
      poster: "./youngmelavoro-poster.jpg",
      comingSoon: true,
    },
  ],
  mobile: [
    { id: "fantazanca", title: "FantaZanca", link: "https://fantazanca.it", video: "./fantazanca-preview.mp4", portrait: true },
    { id: "tripsters", title: "Tripsters.ai", link: "https://tripsters.ai", video: "./tripsters-preview.mp4" },
  ],
};

const skills = {
  ml: [
    "Python", "TensorFlow", "PyTorch", "Scikit-Learn",
    "Deep Learning", "Neural Networks", "CNNs", "RNNs",
    "LSTMs", "Autoencoders", "NLP", "TF-IDF", "Word2Vec", "Transformers",
  ],
  algo: [
    "Logistic Regression", "Decision Trees", "Random Forest", "SVM",
    "XGBoost", "AdaBoost", "K-Means", "K-Medoids", "DBSCAN",
    "Hierarchical Clustering", "Ensemble Learning", "Bagging", "Boosting", "Stacking",
  ],
  bigdata: ["Apache Spark", "Hadoop"],
  langs: ["Python", "C", "C++", "Java", "JavaScript", "PHP", "SQL"],
  web: ["Django", "Flask", "Laravel", "React JS", "Vanilla JS", "REST APIs"],
  security: [
    "Network Security", "Firewalls", "IDS/IPS", "VPNs",
    "Adversarial ML", "Vulnerability Management", "Penetration Testing",
    "Cryptography", "Access Control", "Authentication Protocols",
  ],
  db: ["MongoDB", "MySQL", "PostgreSQL"],
  linux: ["GRUB", "systemd", "Wayland", "X11", "D-Bus"],
  tools: ["Visual Studio", "Git", "Eclipse", "Vim", "Apache Spark", "Hadoop", "UML Modeling"],
  other: ["Fuzzy Systems", "Knowledge Representation and Discovery"],
};

const links = {
  github: "https://github.com/chouaibcher",
  linkedin: "https://www.linkedin.com/in/chouaibcher",
  email: "mailto:chouaibcher@gmail.com",
};

function formatTimeAgo(date, lang) {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  let rtf;
  try {
    rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "short" });
  } catch {
    rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "short" });
  }
  if (diff < 60) return rtf.format(-diff, "second");
  if (diff < 3600) return rtf.format(-Math.floor(diff / 60), "minute");
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), "hour");
  return rtf.format(-Math.floor(diff / 86400), "day");
}

function parseEvent(event, t, lang) {
  const repo = event.repo?.name || "";
  const repoShort = repo.split("/")[1] || repo;
  const repoUrl = `https://github.com/${repo}`;
  const timeAgo = formatTimeAgo(new Date(event.created_at), lang);
  switch (event.type) {
    case "PushEvent": {
      const commits = event.payload?.commits || [];
      const msg = commits[0]?.message?.split("\n")[0] || "";
      return {
        icon: "⬆️",
        label: fmt(t.gh.pushed, { n: commits.length }),
        detail: msg ? `"${msg.length > 60 ? msg.slice(0, 60) + "…" : msg}"` : null,
        repo: repoShort, repoUrl, timeAgo, color: "var(--accent)",
      };
    }
    case "CreateEvent":
      return { icon: "✨", label: fmt(t.gh.created, { type: event.payload?.ref_type || "ref" }), detail: event.payload?.ref ? fmt(t.gh.branch, { name: event.payload.ref }) : null, repo: repoShort, repoUrl, timeAgo, color: "#a855f7" };
    case "WatchEvent":
      return { icon: "⭐", label: t.gh.starred, detail: null, repo: repoShort, repoUrl, timeAgo, color: "#f59e0b" };
    case "ForkEvent":
      return { icon: "🍴", label: t.gh.forked, detail: null, repo: repoShort, repoUrl, timeAgo, color: "#38bdf8" };
    case "IssuesEvent":
      return { icon: "🐛", label: t.gh.issue, detail: event.payload?.issue?.title ? `"${event.payload.issue.title.slice(0, 60)}"` : null, repo: repoShort, repoUrl, timeAgo, color: "#ef4444" };
    case "PullRequestEvent":
      return { icon: "🔀", label: t.gh.pr, detail: event.payload?.pull_request?.title ? `"${event.payload.pull_request.title.slice(0, 60)}"` : null, repo: repoShort, repoUrl, timeAgo, color: "#2563eb" };
    default:
      return { icon: "🔧", label: fmt(t.gh.other, { type: event.type.replace("Event", "") }), detail: null, repo: repoShort, repoUrl, timeAgo, color: "var(--text-soft)" };
  }
}

function LazyVideo({ src, ...props }) {
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      src={isVisible ? src : undefined}
      autoPlay={isVisible}
      muted
      loop
      playsInline
      {...props}
    />
  );
}

function ProjectVideo({ src }) {
  const { t } = useLang();
  const wrapRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = (e) => {
    e.stopPropagation();
    const video = wrapRef.current?.querySelector("video");
    if (!video) return;
    video.muted = !video.muted;
    if (!video.muted) video.play().catch(() => {});
    setMuted(video.muted);
  };

  return (
    <div className="project-video" ref={wrapRef}>
      <LazyVideo src={src} />
      <button
        type="button"
        className="sound-toggle"
        onClick={toggleSound}
        aria-label={muted ? t.soundOn : t.soundOff}
        title={muted ? t.soundOn : t.soundOff}
      >
        {muted ? "🔇" : "🔊"}
      </button>
    </div>
  );
}

function ProjectModal({ project, onClose }) {
  const { t } = useLang();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Pause the card previews so their sound doesn't overlap the popup video.
    const playing = [...document.querySelectorAll(".project-video video")].filter((v) => !v.paused);
    playing.forEach((v) => v.pause());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      playing.forEach((v) => v.play().catch(() => {}));
    };
  }, [onClose]);

  const external = project.link.startsWith("http");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={project.title}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label={t.close}>✕</button>
        <div className={`modal-media${project.portrait ? " portrait" : ""}`}>
          {project.video ? (
            <video src={project.video} controls autoPlay playsInline />
          ) : project.image ? (
            <img src={project.image} alt={project.title} />
          ) : null}
        </div>
        <div className="modal-body">
          <h3 className="modal-title">{project.title}</h3>
          <p className="project-subtitle">{project.subtitle}</p>
          <p className="modal-text">{project.description}</p>
          {project.details && (
            <ul className="card-list">
              {project.details.map((d) => <li key={d}>{d}</li>)}
            </ul>
          )}
          <a
            href={project.link}
            className="modal-cta"
            onClick={external ? undefined : onClose}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
          >
            {t.visitProject}
          </a>
        </div>
      </div>
    </div>
  );
}

function GitHubHeatmap() {
  const { t } = useLang();
  const [grid, setGrid] = useState([]);
  const [total, setTotal] = useState(0);
  const [monthLabels, setMonthLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  useEffect(() => {
    fetch(`https://github-contributions-api.jogruber.de/v4/${GITHUB_USER}?y=last`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data) => {
        const flat = data.contributions || [];
        setTotal(data.total?.lastYear ?? 0);
        const weeks = [];
        let week = [];
        flat.forEach((day, i) => {
          week.push(day);
          if (week.length === 7 || i === flat.length - 1) { weeks.push(week); week = []; }
        });
        const labels = [];
        let lastMonth = -1;
        weeks.forEach((w, wi) => {
          const m = new Date(w[0].date).getMonth();
          if (m !== lastMonth) { labels.push({ label: MONTHS[m], col: wi }); lastMonth = m; }
        });
        setGrid(weeks);
        setMonthLabels(labels);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const levelColors = {
    0: "var(--cell-empty)",
    1: "rgba(22,163,74,0.30)",
    2: "rgba(34,197,94,0.55)",
    3: "rgba(34,197,94,0.80)",
    4: "#22c55e",
  };

  if (loading) return <div className="contrib-loading"><span className="contrib-spinner" /> {t.loadingGraph}</div>;
  if (error) return <div className="contrib-error">⚠️ {t.couldNotLoad} <a href={links.github} target="_blank" rel="noreferrer">{t.viewOnGitHub}</a></div>;

  return (
    <div className="heatmap-wrap">
      <div className="heatmap-months" style={{ gridTemplateColumns: `repeat(${grid.length}, 13px)` }}>
        {monthLabels.map(({ label, col }) => (
          <span key={`${label}-${col}`} className="heatmap-month-label" style={{ gridColumn: col + 1 }}>{label}</span>
        ))}
      </div>
      <div className="heatmap-grid" style={{ gridTemplateColumns: `repeat(${grid.length}, 13px)` }}>
        {grid.map((week, wi) =>
          week.map((day, di) => (
            <span
              key={`${wi}-${di}`}
              className="heatmap-cell"
              style={{ gridColumn: wi + 1, gridRow: di + 1, background: levelColors[day.level] ?? levelColors[0] }}
              title={fmt(t.contribCell, { date: day.date, n: day.count })}
            />
          ))
        )}
      </div>
      <p className="activity-caption">
        {t.contribCaption.split("{n}")[0]}
        <strong style={{ color: "var(--text-primary)" }}>{total.toLocaleString()}</strong>
        {t.contribCaption.split("{n}")[1]}
      </p>
    </div>
  );
}

function GitHubActivity() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`https://api.github.com/users/${GITHUB_USER}/events/public?per_page=15`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data) => { setEvents(data.slice(0, 8)); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  if (loading) return <div className="contrib-loading"><span className="contrib-spinner" /> {t.loadingActivity}</div>;
  if (error) return <div className="contrib-error">⚠️ {t.couldNotLoad} <a href={links.github} target="_blank" rel="noreferrer">{t.viewOnGitHub}</a></div>;

  return (
    <div className="contrib-list">
      {events.map((event, i) => {
        const p = parseEvent(event, t, lang);
        return (
          <div className="contrib-item" key={event.id || i}>
            <span className="contrib-icon" style={{ color: p.color }}>{p.icon}</span>
            <div className="contrib-body">
              <p className="contrib-text">
                <span className="contrib-action">{p.label} </span>
                <a href={p.repoUrl} target="_blank" rel="noreferrer" className="contrib-repo" style={{ color: p.color }}>{p.repo}</a>
              </p>
              {p.detail && <p className="contrib-detail">{p.detail}</p>}
            </div>
            <span className="contrib-time">{p.timeAgo}</span>
          </div>
        );
      })}
      <a href={links.github} target="_blank" rel="noreferrer" className="contrib-view-all">{t.viewAllActivity}</a>
    </div>
  );
}

function LanguageSelect({ lang, onChange, className }) {
  const { t } = useLang();
  return (
    <select
      className={`lang-select${className ? " " + className : ""}`}
      value={lang}
      onChange={(e) => onChange(e.target.value)}
      aria-label={t.language}
      title={t.language}
    >
      {LANGUAGES.map((l) => (
        <option key={l.code} value={l.code}>{l.name}</option>
      ))}
    </select>
  );
}

function App() {
  const [lang, setLang] = useState(getStoredLang);
  const t = T[lang] || T.en;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPrank, setShowPrank] = useState(false);
  const [openProject, setOpenProject] = useState(null);
  const closeProject = React.useCallback(() => setOpenProject(null), []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "dark");
  }, []);

  // Keep <html lang/dir> in sync, and follow the choice made on the quiz screen.
  useEffect(() => { applyLangToDocument(lang); }, [lang]);
  useEffect(() => {
    const onPick = (e) => { if (T[e.detail]) setLang(e.detail); };
    window.addEventListener("site-language", onPick);
    return () => window.removeEventListener("site-language", onPick);
  }, []);

  const changeLang = (code) => {
    saveLang(code);
    setLang(code);
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleThemeClick = React.useCallback(() => {
    setShowPrank(true);
    setTimeout(() => setShowPrank(false), 2800);
  }, []);

  // Trick: clicking the profile picture 5 times (within 2 s of each other) does a barrel roll.
  const avatarClicks = useRef({ count: 0, last: 0 });
  const handleAvatarClick = () => {
    const now = Date.now();
    const c = avatarClicks.current;
    c.count = now - c.last < 2000 ? c.count + 1 : 1;
    c.last = now;
    if (c.count >= 5) {
      c.count = 0;
      window.dispatchEvent(new CustomEvent("trick", { detail: "roll" }));
    }
  };

  return (
    <LangContext.Provider value={{ lang, t }}>
      {/* ── FULLSCREEN MENU — outside .page so overflow:hidden doesn't clip it ── */}
      {menuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <span className="mobile-menu-title">ChouaibCher</span>
              <button
                className="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                aria-label={t.closeMenu}
              >✕</button>
            </div>
            <nav className="mobile-menu-nav">
              <a href="#about"     onClick={() => setMenuOpen(false)}>{t.nav.experience}</a>
              <a href="#projects"  onClick={() => setMenuOpen(false)}>{t.nav.projects}</a>
              <a href="#skills"    onClick={() => setMenuOpen(false)}>{t.nav.skills}</a>
              <a href="#education" onClick={() => setMenuOpen(false)}>{t.nav.education}</a>
            </nav>
            <LanguageSelect lang={lang} onChange={changeLang} className="lang-select-menu" />
            <div className="mobile-menu-footer">
              <a href={links.github}   target="_blank" rel="noreferrer">GitHub</a>
              <a href={links.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
              <a href={links.email}>{t.email}</a>
            </div>
          </div>
        </div>
      )}

      <div className="page">

        {/* ── STICKY NAV ── */}
        <nav className="site-nav">
          <span className="site-nav-name">ChouaibCher</span>

          <div className="site-nav-links">
            <a href="#about">{t.nav.experience}</a>
            <a href="#projects">{t.nav.projects}</a>
            <a href="#skills">{t.nav.skills}</a>
            <a href="#education">{t.nav.education}</a>
          </div>

          <LanguageSelect lang={lang} onChange={changeLang} className="lang-select-nav" />

          <button type="button" className="theme-toggle" onClick={handleThemeClick} aria-label={t.toggleTheme}>
            ☀️
          </button>

          <button
            type="button"
            className="hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t.toggleMenu}
            aria-expanded={menuOpen}
          >
            <span className="ham-line" />
            <span className="ham-line" />
            <span className="ham-line" />
          </button>
        </nav>

        {showPrank && (
          <div className="prank-popup">
            {t.prank} <span>:(</span>
          </div>
        )}

        {/* ── HERO ── */}
<header className="hero">
  <div className="availability-badge hero-badge">
    <span className="availability-dot" />
    <span className="availability-text">Reago SRL</span>
  </div>

  <div className="hero-left">
    <h1 className="name">Chouaib Cheribet Cherif</h1>
    <p className="tagline">{t.tagline}</p>
    <p className="hero-text">{t.heroText}</p>
    <div className="hero-links">
      <a href={links.github} target="_blank" rel="noreferrer">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
        </svg>
        GitHub
      </a>
      <a href={links.linkedin} target="_blank" rel="noreferrer">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
        LinkedIn
      </a>
      <a href={links.email}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7"/>
        </svg>
        {t.email}
      </a>
    </div>
  </div>

  <div className="hero-right">
    <div className="avatar-ring">
      <img className="hero-avatar-img" src="./C.png" alt="Chouaib Cheribet Cherif" onClick={handleAvatarClick} />
    </div>
    <p className="hero-right-caption">{t.heroCaption}</p>
  </div>
</header>

        {/* ── CONTRIBUTION HEATMAP ── */}
        <section className="section activity" id="contributions">
          <h2 className="section-title">
            {t.contributions}
            <span className="contrib-handle">@chouaibcher</span>
          </h2>
          <GitHubHeatmap />
        </section>

        {/* ── RECENT GITHUB ACTIVITY ── */}
        <section className="section">
          <h2 className="section-title">
            {t.recentActivity}
            <span className="contrib-live-dot" style={{ marginLeft: "8px" }} />
          </h2>
          <div className="contrib-card">
            <GitHubActivity />
          </div>
        </section>

        {/* ── ABOUT ── */}
        <section className="section" id="about">
          <h2 className="section-title">{t.aboutTitle}</h2>
          <div className="card">
            <h3 className="card-title">{t.aboutCardTitle}</h3>
            <p className="card-text">{t.aboutP1}</p>
            <p className="card-text">{t.aboutP2}</p>
            <ul className="card-list">
              {t.aboutList.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </section>

        {/* ── PROJECTS ── */}
        <section className="section" id="projects">
          <h2 className="section-title">{t.projectsTitle}</h2>
          {Object.entries(projects).map(([category, items]) => (
            <div className="projects-category" key={category}>
              <h3 className="projects-category-title">{t.categories[category]}</h3>
              <div className="projects-grid">
                {items.map((p) => ({ ...p, ...t.projects[p.id] })).map((project) => (
                  project.comingSoon ? (
                  <article className="project-card soon" key={project.title}>
                    <div className={`project-preview${project.portrait ? " portrait" : ""}`}>
                      {(project.poster || project.image) && (
                        <img src={project.poster || project.image} alt={project.title} className="soon-frame" />
                      )}
                      <span className="soon-badge">{t.soon}</span>
                    </div>
                    <div className="project-body">
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-subtitle">{project.subtitle}</p>
                      <p className="project-description">{project.description}</p>
                      <div className="project-actions">
                        <span className="project-soon-text">{t.comingSoon}</span>
                      </div>
                    </div>
                  </article>
                  ) : (
                  <article className="project-card" key={project.title}>
                    <div
                      className={`project-preview clickable${project.portrait ? " portrait" : ""}`}
                      onClick={() => setOpenProject(project)}
                      role="button"
                      tabIndex={0}
                      aria-label={fmt(t.openDetails, { title: project.title })}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenProject(project); } }}
                    >
                      <span className="preview-hint">{t.clickForDetails}</span>
                      {project.video ? (
                        <ProjectVideo src={project.video} />
                      ) : project.image ? (
                        <img src={project.image} alt={project.title} className="project-preview-img" />
                      ) : (
                        <span className="project-preview-icon">{project.icon}</span>
                      )}
                    </div>
                    <div className="project-body">
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-subtitle">{project.subtitle}</p>
                      <p className="project-description">{project.description}</p>
                      <div className="project-actions">
                        <button type="button" className="project-details-btn" onClick={() => setOpenProject(project)}>
                          {t.details}
                        </button>
                        <a
                          href={project.link}
                          className="project-link"
                          {...(project.link.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                        >
                          {t.viewProject}
                        </a>
                      </div>
                    </div>
                  </article>
                  )
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* ── SKILLS ── */}
        <section className="section" id="skills">
          <h2 className="section-title">{t.skillsTitle}</h2>
          <div className="skills-wrap">
            {Object.entries(skills).map(([group, items]) => (
              <div className="skills-group" key={group}>
                <h3 className="skills-title">{t.skillGroups[group]}</h3>
                <div className="skills-chips">
                  {items.map((skill) => (
                    <span className="chip" key={skill}>{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── EDUCATION ── */}
        <section className="section" id="education">
          <h2 className="section-title">{t.backgroundTitle}</h2>
          <div className="card">
            <h3 className="card-title">{t.eduTitle}</h3>
            <p className="card-text">{t.eduText}</p>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="section footer">
          <h2 className="section-title" style={{ justifyContent: "center" }}>{t.footerTitle}</h2>
          <p className="footer-text">{t.footerText}</p>
          <div className="hero-links" style={{ justifyContent: "center" }}>
            <a href={links.email}>{t.emailMe}</a>
            <a href={links.github} target="_blank" rel="noreferrer">GitHub</a>
            <a href={links.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
          </div>
          <p className="footer-meta">© {new Date().getFullYear()} Chouaib Cheribet Cherif</p>
        </footer>

      </div>

      <TricksLayer
        lang={lang}
        setLang={changeLang}
        t={t}
        projects={projects}
        skills={skills}
        links={links}
        onLightPrank={handleThemeClick}
      />

      {openProject && <ProjectModal project={{ ...openProject, ...t.projects[openProject.id] }} onClose={closeProject} />}
    </LangContext.Provider>
  );
}

export default App;